import { jsx } from 'react/jsx-runtime';
import { stopPropagation } from '../utils/event.js';
import { safari } from '../utils/platform.js';

/**
 * Prevent events from bubbling up to parent elements.
 *
 * @example
 *
 * ```tsx
 * <EventShield eventListeners={['onKeyPress'']}>
 * <EventShield eventListeners={['onMouseDown'] condition={(e) => e.target instanceof HTMLInputElement}}>
 * ```
 *
 * @see https://fpl.figma.design/components/event-shield/
 */
function EventShield({ style, display = 'block', eventListeners, ...props }) {
    const divProps = getPropsFromEventListeners(eventListeners);
    return (jsx("div", { "data-fpl-component": "primitive", ...divProps, ...props, style: { ...style, display }, 
        /**
         * When an event shield has an onClick listener, NVDA adds button role to everything. By adding
         * presentation role explicitly, we remove semantic meaning from the "button". But on Safari,
         * we need it undefined because the combination of role="presentation" + style="display:contents"
         * causes VoiceOver to announce nothing when the wrapped element is selected.
         * https://github.com/nvaccess/nvda/issues/13286
         * https://adrianroselli.com/2022/07/its-mid-2022-and-browsers-mostly-safari-still-break-accessibility-via-display-properties.html
         */
        role: safari ? undefined : 'presentation' }));
}
EventShield.displayName = 'EventShield';
/**
 * Convert `['onClick', 'onPointerCancel', 'onKeyDown']` to
 * `{ onClick: handler, onPointerCancel: handler, onKeyDown: handler }`
 */
function getPropsFromEventListeners(eventListeners) {
    const listenerProps = {};
    for (const eventName of eventListeners) {
        listenerProps[eventName] = stopPropagation;
    }
    return listenerProps;
}

export { EventShield };
//# sourceMappingURL=event-shield.js.map
